//frontend/src/components/admin/UnassignedOrders.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
    Package, 
    MapPin, 
    User, 
    Clock, 
    RefreshCw, 
    DollarSign, 
    Truck, 
    Calendar,
    Search,
    Phone,
    ArrowLeft
} from 'lucide-react';
import axios from 'axios';
import { Card } from '../../components/ui/card';

const UnassignedOrders = () => {
    const [orders, setOrders] = useState([]);
    const [deliveryStaff, setDeliveryStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [assigning, setAssigning] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        fetchUnassignedOrders();
        fetchDeliveryStaff();
    }, []);

    const fetchUnassignedOrders = async () => {
        try {
            const { data } = await axios.get('/api/v1/admin/unassigned-orders', {
                withCredentials: true
            });

            if (data.success) {
                setOrders(data.orders);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error fetching unassigned orders');
        } finally {
            setLoading(false);
        }
    };

    const fetchDeliveryStaff = async () => {
        try {
            const { data } = await axios.get('/api/v1/admin/delivery-staff', {
                withCredentials: true
            });

            if (data.success) {
                setDeliveryStaff(data.deliveryStaff.filter(staff => !staff.isBlocked));
            }
        } catch (error) {
            toast.error('Error fetching delivery staff list');
        }
    };

    const handleAssignStaff = async (orderId, deliveryStaffId) => {
        try {
            setAssigning(true);
            const { data } = await axios.post(
                '/api/v1/admin/assign-delivery-staff',
                { orderId, deliveryStaffId },
                { withCredentials: true }
            );

            if (data.success) {
                toast.success('Delivery staff assigned successfully');
                fetchUnassignedOrders();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error assigning delivery staff');
        } finally {
            setAssigning(false);
        }
    };

    const getAreaName = (pincode) => {
        const areas = {
            '562130': 'Gollarahatti',
            '562130': 'Kadabagere',
            '560112': 'Chikka Gollarahatti',
            '560091': 'Machohalli',
            '560091': 'Anjana Nagar',
            '560091': 'Byadarahalli',
            '562130': 'Tavarekere',
            '560091': 'Herohalli',
            '560091': 'Viswaneedam',
            '560091': 'Sunkadakatte',
            '560091': 'Muddayanapalya',
            '560091': 'Annapurneshwari Nagar',
            '560091': 'Kottigepalya',
            '560091': 'Sumanahalli'
        };
        return areas[pincode] || pincode;
    };

    const filteredOrders = orders.filter(order => {
        if (!order || !order.user) return false;
        
        return (
            order.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (order.user.name && order.user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            getAreaName(order.shippingInfo.postalCode).toLowerCase().includes(searchTerm.toLowerCase())
        );
    });

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <RefreshCw className="h-8 w-8 text-[#dc2626] animate-spin" />
            </div>
        );
    }

    return (
        <div className="container mx-auto px-3 py-6">
            {/* Back to Dashboard Button - Right aligned */}
            <div className="flex justify-end mb-4">
                <button
                    onClick={() => navigate('/admin/dashboard')}
                    className="flex items-center gap-1.5 text-gray-600 hover:text-[#dc2626] transition-colors text-sm"
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Dashboard</span>
                </button>
            </div>

            <Card className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="p-4 sm:p-6 border-b border-gray-200">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-center space-x-2">
                            <Package className="h-6 w-6 text-[#dc2626]" />
                            <div>
                                <h2 className="text-xl font-bold text-[#dc2626] font-['Crimson_Text']">
                                    Unassigned Orders
                                </h2>
                                <p className="text-sm text-gray-500">
                                    Orders pending assignment: {orders.length}
                                </p>
                            </div>
                        </div>

                        <div className="relative w-full md:w-64">
                            <input
                                type="text"
                                placeholder="Search orders..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] transition-colors text-sm"
                            />
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        </div>
                    </div>
                </div>

                <div className="p-4 sm:p-6">
                    {filteredOrders.length > 0 ? (
                        <div className="grid grid-cols-1 gap-4 sm:gap-6">
                            {filteredOrders.map((order) => (
                                <div key={order._id} 
                                    className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-shadow"
                                >
                                    <div className="flex flex-col gap-4">
                                        {/* Order Header */}
                                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                                                Order #{order.orderId}
                                            </h3>
                                            <span className="px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                Unassigned
                                            </span>
                                            <span className="px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-medium bg-[#dc2626] text-white">
                                                ₹{order.totalPrice}
                                            </span>
                                        </div>

                                        {/* Order Details */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {/* Customer & Address */}
                                            <div className="space-y-3">
                                                <div className="flex items-start space-x-3">
                                                    <User className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mt-0.5" />
                                                    <div>
                                                        <p className="text-xs sm:text-sm text-gray-500">Customer</p>
                                                        <p className="font-medium text-sm text-gray-900">{order.user.name}</p>
                                                        <p className="text-xs sm:text-sm text-gray-500">{order.shippingInfo.phoneno}</p>
                                                    </div>
                                                </div>

                                                <div className="flex items-start space-x-3">
                                                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mt-0.5" />
                                                    <div>
                                                        <p className="text-xs sm:text-sm text-gray-500">Delivery Area</p>
                                                        <p className="font-medium text-sm text-gray-900">
                                                            {getAreaName(order.shippingInfo.postalCode)}
                                                        </p>
                                                        <p className="text-xs sm:text-sm text-gray-500">
                                                            {order.shippingInfo.address}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Order Date & Payment */}
                                            <div className="space-y-3">
                                                <div className="flex items-start space-x-3">
                                                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mt-0.5" />
                                                    <div>
                                                        <p className="text-xs sm:text-sm text-gray-500">Order Date</p>
                                                        <p className="font-medium text-sm text-gray-900">
                                                            {new Date(order.createdAt).toLocaleDateString('en-IN')}
                                                        </p>
                                                        <p className="text-xs sm:text-sm text-gray-500">
                                                            {new Date(order.createdAt).toLocaleTimeString('en-IN')}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex items-start space-x-3">
                                                    <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mt-0.5" />
                                                    <div>
                                                        <p className="text-xs sm:text-sm text-gray-500">Payment Method</p>
                                                        <p className="font-medium text-sm text-gray-900">
                                                            {order.paymentInfo.paymentMethod}
                                                        </p>
                                                        <p className={`text-xs sm:text-sm ${
                                                            order.paymentInfo.status === 'succeeded' 
                                                            ? 'text-green-600' 
                                                            : 'text-yellow-600'
                                                        }`}>
                                                            {order.paymentInfo.status === 'succeeded' ? 'Paid' : 'Pending'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Assign Staff */}
                                        <div className="pt-3 border-t border-gray-100">
                                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                                                Assign Delivery Staff
                                            </label>
                                            <select
                                                className="w-full px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] transition-colors text-sm"
                                                onChange={(e) => {
                                                    if (e.target.value) {
                                                        handleAssignStaff(order._id, e.target.value);
                                                    }
                                                }}
                                                disabled={assigning}
                                            >
                                                <option value="">Select Staff</option>
                                                {deliveryStaff.map((staff) => (
                                                    <option key={staff._id} value={staff._id}>
                                                        {staff.name} ({staff.loginId})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 sm:py-12">
                            <Package className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No Unassigned Orders</h3>
                            <p className="mt-1 text-xs sm:text-sm text-gray-500">
                                {searchTerm ? 'No orders match your search criteria.' : 'All orders have been assigned to delivery staff.'}
                            </p>
                        </div>
                    )}
                </div>
            </Card>

            {/* Mobile Optimization Styles */}
            <style jsx>{`
                @media (max-width: 640px) {
                    .text-sm {
                        font-size: 0.75rem;
                        line-height: 1rem;
                    }
                    
                    .p-4 {
                        padding: 0.75rem;
                    }
                    
                    .gap-4 {
                        gap: 0.75rem;
                    }
                    
                    .space-y-3 > :not([hidden]) ~ :not([hidden]) {
                        --tw-space-y-reverse: 0;
                        margin-top: calc(0.5rem * calc(1 - var(--tw-space-y-reverse)));
                        margin-bottom: calc(0.5rem * var(--tw-space-y-reverse));
                    }
                    
                    .py-1\\.5 {
                        padding-top: 0.25rem;
                        padding-bottom: 0.25rem;
                    }
                    
                    .px-3 {
                        padding-left: 0.5rem;
                        padding-right: 0.5rem;
                    }
                    
                    .text-base {
                        font-size: 0.875rem;
                        line-height: 1.25rem;
                    }
                    
                    .mb-4 {
                        margin-bottom: 0.75rem;
                    }
                    
                    .mb-1\\.5 {
                        margin-bottom: 0.25rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default UnassignedOrders;